import { useState, useEffect, useRef, useCallback } from "react";
import {
  PaperAirplaneIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  SparklesIcon,
  DocumentTextIcon,
  ClockIcon,
  UserGroupIcon,
  ClipboardDocumentIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import Tooltip from "../components/Tooltip";
import { Link, useLocation } from "react-router-dom";
import PlanGatedPage from "../components/PlanGatedPage";
import { useStudioPlan } from "../hooks/useStudioPlan";
import { isPaymentLinkTag } from "../plans/tagFilters";

interface StudioUser {
  id: string;
  studio_id: string | null;
}

interface SMSTag {
  id: string;
  tag_name: string;
  tag_key: string;
  value?: string;
}

interface Student {
  id: string;
  name: string;
  phone: string;
  belt?: string;
  [key: string]: any;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  interest?: string;
  [key: string]: any;
}

interface SMSTemplate {
  id: string;
  name: string;
  template: string;
}

interface Recipient {
  id: string;
  name: string;
  phone: string;
  type: "student" | "lead" | "staff";
  [key: string]: any;
}

interface Studio {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
}

interface SMSConfig {
  isConfigured: boolean;
  provider: string;
  accountSid?: string;
  authToken?: string;
  fromNumber?: string;
}

interface Class {
  id: string;
  class_name: string;
  start_time: string;
  end_time: string;
  studio_id: string;
  days: string[];
  assigned_instructors?: any[];
}

interface Event {
  id: string;
  event_name: string;
  from_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  studio_id: string;
  type: string;
  assigned_instructors?: any[];
}

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  messageContent: 1000, // Reasonable limit for SMS content
  templateName: 100,
  aiInput: 1000,
  searchTerm: 100,
  classSearchTerm: 100,
  eventSearchTerm: 100,
  templateSearchTerm: 100,
  scheduledDate: 10,
  scheduledTime: 8,
  /** Room for "XXX-XXX-XXXX" plus optional extra digits / space */
  customPhone: 24,
};

/** Match backend `sendSmsUtil.formatPhoneNumber` for consistent tag keys and Twilio */
function formatPhoneForSmsE164(phone: string): string {
  let cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) {
    if (cleaned.length === 10) {
      cleaned = "+1" + cleaned;
    } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
      cleaned = "+" + cleaned;
    }
  }
  return cleaned;
}

function phoneDigitsKey(phone: string): string {
  return phone.replace(/\D/g, "");
}

/** 10-digit US national key when possible — matches SMS History / backend lookup */
function phoneMatchKey(phone: string): string {
  const digits = phoneDigitsKey(phone);
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

/** Match backend `validatePhoneNumber` (digits-only length) */
function isValidSmsPhoneDigits(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  return /^\+?1?\d{10,15}$/.test(digits);
}

/** Partial NANP digits → XXX-XXX-XXXX as user types */
function formatUsPartialDigits(d: string): string {
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

/**
 * Custom phone field: format as XXX-XXX-XXXX while typing (digits only, max 15).
 * Leading country code 1 is absorbed so the next 10 digits get the dash pattern.
 */
function formatCustomPhoneInputAsTyped(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 15);
  if (d.length === 0) return "";

  let national = d;
  if (d.length >= 11 && d.startsWith("1")) {
    national = d.slice(1);
  }

  if (national.length <= 10) {
    return formatUsPartialDigits(national);
  }
  const first = national.slice(0, 10);
  const rest = national.slice(10);
  return `${formatUsPartialDigits(first)} ${rest}`;
}

/** US/Canada 10-digit display as XXX-XXX-XXXX; other formats left readable */
function formatPhoneDisplayDashes(phone: string): string {
  const d = phone.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    const n = d.slice(1, 11);
    return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6, 10)}`;
  }
  if (d.length === 10) {
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 10)}`;
  }
  const t = phone.trim();
  return t || phone;
}

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions
const SMS_TEMPLATES_LIMIT = 10;

// Input sanitization function (matching PublicWaiver.tsx approach)
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For message content and AI input, preserve formatting but remove dangerous content
  if (fieldType === 'messageContent' || fieldType === 'aiInput') {
    // Remove dangerous patterns but preserve line breaks and basic formatting
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    // Don't trim to preserve user intent
  } else if (fieldType === 'templateName' || fieldType === 'searchTerm') {
    // For names and search terms, preserve spaces but remove dangerous content
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // Don't trim for search terms - preserve user intent
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function (matching PublicWaiver.tsx approach)
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'messageContent':
      // Check for dangerous patterns in message content
      const messageDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !messageDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'templateName':
      // Template names should be alphanumeric, spaces, hyphens, underscores
      const templateNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
      return templateNameRegex.test(sanitized) && sanitized.length >= 1;
    case 'aiInput':
      // Check for dangerous patterns in AI input
      const aiDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !aiDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'scheduledDate':
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    case 'scheduledTime':
      const timeRegex = /^\d{1,2}:\d{2}$/;
      return timeRegex.test(sanitized);
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`SMS Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

// Utility function to replace template tags
function replaceTemplateTags(template: string, data: Record<string, string>) {
  return template.replace(/{{(.*?)}}/g, (_, tag) => data[tag.trim()] || "");
}

// Date formatter function
const formatDate = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Time formatter function (without AM/PM handling)
const formatTime = (value: string): string => {
  // Remove all non-alphanumeric characters except colon
  const cleaned = value.replace(/[^0-9:]/gi, '');
  
  // Extract numbers
  const numbers = cleaned.replace(/[^0-9]/g, '');
  
  // Limit to 4 digits (HHMM)
  const limited = numbers.slice(0, 4);
  
  // Format as HH:MM
  if (limited.length >= 3) {
    const hours = limited.slice(0, 2);
    const minutes = limited.slice(2, 4);
    const hourNum = parseInt(hours);
    
    // Only convert 24-hour to 12-hour if it's clearly 24-hour format
    if (hourNum > 12 && hourNum <= 23) {
      const hour12 = hourNum - 12;
      return `${hour12.toString().padStart(2, '0')}:${minutes}`;
    } else if (hourNum === 0) {
      return `12:${minutes}`;
    } else {
      return `${hours}:${minutes}`;
    }
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert MM/DD/YYYY to YYYY-MM-DD for backend
const convertDateToISO = (dateString: string): string => {
  if (!dateString || dateString.length !== 10) return '';
  
  const parts = dateString.split('/');
  if (parts.length !== 3) return '';
  
  const month = parts[0];
  const day = parts[1];
  const year = parts[2];
  
  return `${year}-${month}-${day}`;
};

// Convert military time to 12-hour format
const formatTime12Hour = (timeString: string): string => {
  if (!timeString) return '';
  
  try {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const minute = parseInt(minutes);
    
    if (isNaN(hour) || isNaN(minute)) return timeString;
    
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return timeString;
  }
};

export default function SMS() {
  const { studioUser } = useStudioUser();
  const { getAuthToken } = useAuth();
  const { hasCapability } = useStudioPlan();
  const location = useLocation();
  const [studio, setStudio] = useState<Studio | null>(null);
  const [templates, setTemplates] = useState<SMSTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [selectedRecipientNames, setSelectedRecipientNames] = useState<string[]>([]);
  const [selectedRecipientPhones, setSelectedRecipientPhones] = useState<
    string[]
  >([]);
  const [phoneRecipientLabels, setPhoneRecipientLabels] = useState<
    Record<string, string>
  >({});
  const [messageContent, setMessageContent] = useState("");
  const [smsTags, setSmsTags] = useState<SMSTag[]>([]);
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiInput, setAIInput] = useState("");
  const [aiOutput, setAIOutput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [customPhoneInput, setCustomPhoneInput] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduledAMPM, setScheduledAMPM] = useState("AM");
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [typeFilter, setTypeFilter] = useState<"all" | "student" | "lead" | "staff">(
    "all"
  );
  const [templateToDelete, setTemplateToDelete] = useState<SMSTemplate | null>(
    null
  );
  const [smsConfig, setSmsConfig] = useState<SMSConfig>({
    isConfigured: false,
    provider: "twilio",
  });
  const [smsConfigLoading, setSmsConfigLoading] = useState(true);
  const [smsCheckComplete, setSmsCheckComplete] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Template limits
  const smsTemplatesLimitReached = templates.length >= SMS_TEMPLATES_LIMIT;

  // Studio timezone state
  const [studioTimezone, setStudioTimezone] = useState("America/New_York");

  // SMS Compliance notice dismissal state
  const [showComplianceNotice, setShowComplianceNotice] = useState(true);

  // Class filtering state
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classSearchTerm, setClassSearchTerm] = useState("");

  // Event filtering state
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventSearchTerm, setEventSearchTerm] = useState("");

  // Character count for SMS (160 chars per message)
  const charCount = messageContent.length;
  const messageCount = Math.ceil(charCount / 160);

  useEffect(() => {
    if (studioUser?.studio_id) {
      fetchStudioData();
      loadSMSTags();
      loadTemplates();
      loadRecipients();
      loadSMSConfig();
      loadClasses();
      loadEvents();
      fetchCsrfToken();
    }
  }, [studioUser]);

  // Check localStorage for compliance notice dismissal
  useEffect(() => {
    const isDismissed = localStorage.getItem('sms_compliance_notice_dismissed');
    if (isDismissed === 'true') {
      setShowComplianceNotice(false);
    }
  }, []);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const fetchStudioData = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.ok) {
        const studioData = await response.json();
        setStudio(studioData);
        // Set studio timezone for scheduling
        setStudioTimezone(studioData.timezone || "America/New_York");
      }
    } catch (error) {
      console.error("Error fetching studio data:", error);
    }
  };

  const loadSMSConfig = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) {
      setSmsConfigLoading(false);
      setSmsCheckComplete(true);
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config?studio_id=${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.ok) {
        const config = await response.json();
        
        // Check if SMS is configured for EITHER provider type
        const isConfigured = 
          (config.provider === 'studio_managed' && !!config.fromnumber) || // Studio-managed
          (config.provider === 'own_twilio' && !!config.accountsid) ||      // Own Twilio
          (!config.provider && !!config.accountsid);                        // Legacy (no provider field)
        
        setSmsConfig({
          isConfigured,
          provider: config.provider === 'studio_managed' ? 'studio_managed' : 'twilio',
          accountSid: config.accountsid,
          fromNumber: config.fromnumber,
        });
        
        // Don't show notification automatically - let users explore the interface first
      } else {
        setSmsConfig({
          isConfigured: false,
          provider: "twilio",
        });
        // Don't show notification for connection check failures - let users explore the interface first
      }
    } catch (error) {
      console.error("Error loading SMS config:", error);
      setSmsConfig({
        isConfigured: false,
        provider: "twilio",
      });
      setNotification({
        show: true,
        title: "Connection Error",
        message: "Failed to check SMS connection. Please try refreshing the page.",
      });
    } finally {
      setSmsConfigLoading(false);
      setSmsCheckComplete(true);
    }
  };

  const loadSMSTags = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-tags`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.ok) {
        const tags = await response.json();
        setSmsTags(tags);
      }
    } catch (error) {
      console.error("Error loading SMS tags:", error);
    }
  };

  const loadClasses = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch classes");
      }

      const data = await response.json();
      setClasses(data);
    } catch (error) {
      // Error handling without console.error
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadEvents = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch events");
      }

      const data = await response.json();
      setEvents(data);
    } catch (error) {
      // Error handling without console.error
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadTemplates = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;
    setLoadingTemplates(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-templates/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.ok) {
        const templates = await response.json();
        setTemplates(templates);
      }
    } catch (error) {
      console.error("Error loading SMS templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load templates when the modal is opened
  useEffect(() => {
    if (showTemplatesModal) {
      loadTemplates();
    }
  }, [showTemplatesModal]);

  // Handle reply to phone number from navigation state
  // Track the last processed phone number to prevent duplicates
  const lastProcessedPhone = useRef<string | null>(null);
  const pendingBirthdayPhonesRef = useRef<string[] | null>(null);
  const pendingBirthdayStudentsRef = useRef<Array<{ name: string; phone: string; birthday: string }> | null>(null);
  
  // Handle reply to phone from navigation state
  useEffect(() => {
    const state = location.state as {
      replyToPhone?: string;
      replyToDisplayName?: string;
    } | null;

    if (
      !state?.replyToPhone ||
      loadingRecipients ||
      state.replyToPhone === lastProcessedPhone.current
    ) {
      return;
    }

    const phoneNumber = state.replyToPhone;
    const phoneKey = phoneMatchKey(phoneNumber);

    lastProcessedPhone.current = phoneNumber;
    window.history.replaceState({}, document.title);

    const matchingRecipient = recipients.find(
      (r) => r.phone && phoneMatchKey(r.phone) === phoneKey
    );

    if (matchingRecipient) {
      setSelectedRecipientNames([matchingRecipient.name]);
      setSelectedRecipients([matchingRecipient.id]);
      setSelectedRecipientPhones([]);
      setPhoneRecipientLabels({});
      return;
    }

    const applyPhoneReply = (displayName?: string) => {
      const normalized = formatPhoneForSmsE164(phoneNumber);
      setSelectedRecipientPhones([normalized]);
      setSelectedRecipientNames([]);
      setSelectedRecipients([]);
      if (displayName?.trim()) {
        setPhoneRecipientLabels({ [phoneKey]: displayName.trim() });
      } else {
        setPhoneRecipientLabels({});
      }
    };

    if (state.replyToDisplayName?.trim()) {
      applyPhoneReply(state.replyToDisplayName);
      return;
    }

    const resolveContactName = async () => {
      let displayName: string | undefined;
      const authToken = getAuthToken();
      if (authToken && studioUser?.studio_id) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/contacts?studio_id=${studioUser.studio_id}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          if (response.ok) {
            const data = await response.json();
            const contact = (data.contacts || []).find(
              (c: { phone_number: string; display_name: string }) =>
                phoneMatchKey(c.phone_number) === phoneKey
            );
            if (contact?.display_name) {
              displayName = contact.display_name;
            }
          }
        } catch (error) {
          console.error("Error loading SMS contact for reply:", error);
        }
      }
      applyPhoneReply(displayName);
    };

    resolveContactName();
  }, [location.state, recipients, loadingRecipients, studioUser?.studio_id, getAuthToken]);
  
  // Handle birthday phones from navigation state
  useEffect(() => {
    const state = location.state as { birthdayPhones?: string[]; birthdayStudents?: Array<{ name: string; phone: string; birthday: string }> } | null;
    
    // Store birthday phones and students from navigation state
    if (state?.birthdayPhones && Array.isArray(state.birthdayPhones)) {
      pendingBirthdayPhonesRef.current = state.birthdayPhones;
    }
    if (state?.birthdayStudents && Array.isArray(state.birthdayStudents)) {
      pendingBirthdayStudentsRef.current = state.birthdayStudents;
    }
    
    // Clear the navigation state immediately to prevent re-processing
    if (state?.birthdayPhones || state?.birthdayStudents) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Process pending birthday phones when recipients are loaded
  useEffect(() => {
    if (pendingBirthdayPhonesRef.current && !loadingRecipients) {
      const birthdayPhones = pendingBirthdayPhonesRef.current;
      const birthdayStudents = pendingBirthdayStudentsRef.current;
      
      // Normalize phone numbers for comparison (remove non-digits)
      const normalizePhone = (phone: string) => phone.replace(/\D/g, '');
      const normalizedBirthdayPhones = birthdayPhones.map(normalizePhone);
      
      if (recipients.length > 0) {
        // Match phones with recipients, but only if they match the birthday students by name and phone
        const matchedRecipients: string[] = [];
        const matchedNames: string[] = [];
        const unmatchedPhones: string[] = [];
        
        birthdayPhones.forEach((phone) => {
          const normalizedPhone = normalizePhone(phone);
          
          // Try to find a matching recipient
          const matchingRecipient = recipients.find((r) => {
            const recipientPhone = r.phone ? normalizePhone(r.phone) : '';
            if (recipientPhone !== normalizedPhone) return false;
            
            // If we have birthday students data, also verify the name matches
            if (birthdayStudents && birthdayStudents.length > 0) {
              const matchingStudent = birthdayStudents.find(
                bs => normalizePhone(bs.phone) === normalizedPhone && bs.name === r.name
              );
              return !!matchingStudent;
            }
            
            return true;
          });
          
          if (matchingRecipient) {
            matchedRecipients.push(matchingRecipient.id);
            matchedNames.push(matchingRecipient.name);
          } else {
            unmatchedPhones.push(phone);
          }
        });
        
        // Set matched recipients
        if (matchedRecipients.length > 0) {
          setSelectedRecipients(matchedRecipients);
          setSelectedRecipientNames(matchedNames);
        }
        
        // Set unmatched phones (phone-only recipients)
        if (unmatchedPhones.length > 0) {
          setSelectedRecipientPhones(unmatchedPhones);
        } else {
          setSelectedRecipientPhones([]);
        }
      } else {
        // If recipients are empty, set phones directly
        setSelectedRecipientPhones(birthdayPhones);
        setSelectedRecipients([]);
        setSelectedRecipientNames([]);
      }
      
      // Clear the pending phones after processing
      pendingBirthdayPhonesRef.current = null;
      pendingBirthdayStudentsRef.current = null;
    }
  }, [recipients, loadingRecipients]);

  const loadRecipients = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;
    try {
      const [studentsResponse, leadsResponse, staffResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser.studio_id}?limit=10000`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioUser.studio_id}?limit=10000`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/${studioUser.studio_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
      ]);

      // Check if all responses are successful
      if (!studentsResponse.ok || !leadsResponse.ok || !staffResponse.ok) {
        throw new Error(`API request failed: Students: ${studentsResponse.status}, Leads: ${leadsResponse.status}, Staff: ${staffResponse.status}`);
      }

      const [studentsDataRaw, leadsDataRaw, staffDataRaw] = await Promise.all([
        studentsResponse.json(),
        leadsResponse.json(),
        staffResponse.json(),
      ]);

      const students = Array.isArray(studentsDataRaw.data)
        ? studentsDataRaw.data
        : [];
      const leads = Array.isArray(leadsDataRaw.data) ? leadsDataRaw.data : [];
      const staff = Array.isArray(staffDataRaw) ? staffDataRaw : [];

      const studentRecipients: Recipient[] = students
        .filter((student: Student) => student && student.phone)
        .map((student: Student) => ({
          id: student.id,
          name: student.name,
          phone: student.phone,
          type: "student" as const,
          belt: student.belt,
        }));

      const leadRecipients: Recipient[] = leads
        .filter((lead: Lead) => lead && lead.phone)
        .map((lead: Lead) => ({
          id: lead.id,
          name: lead.name,
          phone: lead.phone,
          type: "lead" as const,
          interest: lead.interest,
        }));

      const staffRecipients: Recipient[] = staff
        .filter((staffMember: any) => staffMember && staffMember.phone)
        .map((staffMember: any) => ({
          id: staffMember.id,
          name: staffMember.name,
          phone: staffMember.phone,
          type: "staff" as const,
        }));

      setRecipients([...studentRecipients, ...leadRecipients, ...staffRecipients]);
    } catch (error) {
      console.error("Error loading recipients:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to load recipients. Please try again.",
      });
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleInsertTag = (tag: SMSTag) => {
    const tagPlaceholder = `{{${tag.tag_key}}}`;
    setMessageContent((prev) => {
      const newContent = prev + tagPlaceholder;
      // Validate the new content length
      if (newContent.length > MAX_LENGTHS.messageContent) {
        setNotification({
          show: true,
          title: "Error",
          message: "Message content is too long. Please shorten your message.",
        });
        return prev;
      }
      return newContent;
    });
    setShowTagsModal(false);
  };

  const handleSend = async () => {
    // Enhanced input validation
    if (!messageContent.trim() || (selectedRecipientNames.length === 0 && selectedRecipientPhones.length === 0)) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please enter a message and select recipients.",
      });
      return;
    }

    // Enhanced message content validation
    if (!validateInput(messageContent, 'messageContent')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'messageContent', value: messageContent.substring(0, 50) + '...' });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid message content. Please check your message and try again.",
      });
      return;
    }

    if (!smsConfig.isConfigured) {
      setNotification({
        show: true,
        title: "Error",
        message:
          "SMS is not configured. Please set up your SMS integration in Settings → Integrations.",
      });
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before sending another SMS",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'sendSMS' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);
    setIsSending(true);

    try {
      // Prepare recipients and tag data
      // Get recipient IDs from the selected names
      const selectedRecipientIds = recipients
        .filter((r) => selectedRecipientNames.includes(r.name))
        .map((r) => r.id);
      
      const allRecipients = [...selectedRecipientIds];
      const tagDataByRecipient: Record<string, Record<string, string>> = {};

      // Handle recipients with IDs (from individual selection)
      for (const recipientId of selectedRecipientIds) {
        const recipient = recipients.find((r) => r.id === recipientId);
        const isStudent = recipient?.type === "student";
        const isLead = recipient?.type === "lead";
        const isStaff = recipient?.type === "staff";

        const tagData: Record<string, string> = {
          // camelCase tags
          studioName: studio?.name || "",
          studioAddress: studio?.address || "",
          studioPhone: studio?.phone || "",
          studioEmail: studio?.email || "",
          studentName: isStudent ? recipient?.name || "" : "",
          leadName: isLead ? recipient?.name || "" : "",
          leadEmail: isLead ? recipient?.email || "" : "",
          staffName: isStaff ? recipient?.name || "" : "",
          staffEmail: isStaff ? recipient?.email || "" : "",
          beltRank: isStudent ? recipient?.belt || "" : "",
          // snake_case tags
          studio_name: studio?.name || "",
          studio_address: studio?.address || "",
          studio_phone: studio?.phone || "",
          studio_email: studio?.email || "",
          student_name: isStudent ? recipient?.name || "" : "",
          lead_name: isLead ? recipient?.name || "" : "",
          lead_email: isLead ? recipient?.email || "" : "",
          staff_name: isStaff ? recipient?.name || "" : "",
          staff_email: isStaff ? recipient?.email || "" : "",
          belt_rank: isStudent ? recipient?.belt || "" : "",
          // Add recipient type for smart tag replacement
          _recipient_type: recipient?.type || "unknown",
          _recipient_id: recipient?.id || "",
        };

        tagDataByRecipient[recipientId] = tagData;
      }

      // Handle phone-only recipients (from class/event selection)
      for (const phone of selectedRecipientPhones) {
        const phoneId = `phone_${phone}`; // Create a unique ID for phone-only recipients
        allRecipients.push(phoneId);
        
        const tagData: Record<string, string> = {
          // camelCase tags
          studioName: studio?.name || "",
          studioAddress: studio?.address || "",
          studioPhone: studio?.phone || "",
          studioEmail: studio?.email || "",
          studentName: "", // No name available for phone-only recipients
          leadName: "", // No name available for phone-only recipients
          leadEmail: "", // No email available for phone-only recipients
          beltRank: "", // No belt available for phone-only recipients
          // snake_case tags
          studio_name: studio?.name || "",
          studio_address: studio?.address || "",
          studio_phone: studio?.phone || "",
          studio_email: studio?.email || "",
          student_name: "", // No name available for phone-only recipients
          lead_name: "", // No name available for phone-only recipients
          lead_email: "", // No email available for phone-only recipients
          belt_rank: "", // No belt available for phone-only recipients
        };

        tagDataByRecipient[phoneId] = tagData;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/send-sms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            message: sanitizeInput(messageContent, 'messageContent'),
            recipients: allRecipients,
            studio_id: studioUser?.studio_id,
            tagDataByRecipient,
            phoneOnlyRecipients: selectedRecipientPhones, // Add phone-only recipients separately
            csrfToken,
          }),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          title: "Success",
          message: "Message sent to recipients.",
        });
        // Clear the form after successful send
        setMessageContent("");
        setSelectedRecipients([]);
        setSelectedRecipientNames([]);
        setSelectedRecipientPhones([]);
        setPhoneRecipientLabels({});
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to send SMS");
      }
    } catch (error) {
      setNotification({
        show: true,
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to queue SMS",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleNewMessage = () => {
    setMessageContent("");
    setSelectedRecipients([]);
    setSelectedRecipientNames([]);
    setSelectedRecipientPhones([]);
    setPhoneRecipientLabels({});
    setCustomPhoneInput("");
    setIsRecipientModalOpen(true);
  };

  const handleSelectRecipients = () => {
    // Pre-populate selectedRecipients with currently selected recipients
    const currentRecipientIds = recipients
      .filter((r) => selectedRecipientNames.includes(r.name))
      .map((r) => r.id);
    setSelectedRecipients(currentRecipientIds);
    setCustomPhoneInput("");
    setIsRecipientModalOpen(true);
  };

  const handleRecipientModalClose = () => {
    setIsRecipientModalOpen(false);
    setSelectedRecipients([]);
    setSelectedRecipientNames([]);
    setSearchTerm("");
    setCustomPhoneInput("");
  };

  const handleAddCustomPhoneToSelection = () => {
    const raw = customPhoneInput.trim();
    if (!raw) {
      return;
    }
    const digitsOnly = raw.replace(/\D/g, "");
    if (digitsOnly.length > 15) {
      setNotification({
        show: true,
        title: "Error",
        message: "Phone number is too long.",
      });
      return;
    }
    const normalized = formatPhoneForSmsE164(digitsOnly);
    if (!normalized || !isValidSmsPhoneDigits(normalized)) {
      setNotification({
        show: true,
        title: "Error",
        message:
          "Please enter a valid phone number (e.g. 5551234567 or +1 555 123 4567).",
      });
      return;
    }
    const newKey = phoneDigitsKey(normalized);
    if (
      selectedRecipientPhones.some((p) => phoneDigitsKey(p) === newKey)
    ) {
      setNotification({
        show: true,
        title: "Error",
        message: "That number is already added.",
      });
      return;
    }
    const phonesFromSelectedNames = recipients
      .filter((r) => selectedRecipientNames.includes(r.name))
      .map((r) => r.phone)
      .filter(Boolean);
    if (
      phonesFromSelectedNames.some((p) => phoneDigitsKey(p) === newKey)
    ) {
      setNotification({
        show: true,
        title: "Error",
        message: "That number is already selected in the list.",
      });
      return;
    }
    if (
      selectedRecipients.some((id) => {
        const r = recipients.find((x) => x.id === id);
        return r?.phone && phoneDigitsKey(r.phone) === newKey;
      })
    ) {
      setNotification({
        show: true,
        title: "Error",
        message: "That number is already checked in the list below.",
      });
      return;
    }
    setSelectedRecipientPhones((prev) => [...prev, normalized]);
    setCustomPhoneInput("");
  };



  const handleRecipientSelect = (recipientId: string) => {
    setSelectedRecipients((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredRecipients.map((r) => r.id);
    setSelectedRecipients(allIds);
  };

  const handleRecipientsConfirm = () => {
    // Get the names of newly selected recipients
    const newSelectedNames = recipients
      .filter((recipient) => selectedRecipients.includes(recipient.id))
      .map((recipient) => recipient.name);
    
    // Add new names to existing selectedRecipientNames (avoid duplicates)
    setSelectedRecipientNames(prev => {
      const combined = [...prev, ...newSelectedNames];
      return Array.from(new Set(combined)); // Remove duplicates
    });
    
    setIsRecipientModalOpen(false);
    setSearchTerm("");
    setCustomPhoneInput("");
    setSelectedRecipients([]); // Clear selections so modal starts fresh next time
  };

  const handleClassModalOpen = () => {
    setShowClassModal(true);
  };

  const handleClassModalClose = () => {
    setShowClassModal(false);
    setSelectedClasses([]);
  };

  const handleClassSelect = (classId: string) => {
    setSelectedClasses((prev) => {
      if (prev.includes(classId)) {
        return prev.filter((id) => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
  };

  const handleSelectAllClasses = () => {
    setSelectedClasses(filteredClasses.map((classItem) => classItem.id));
  };

  const handleClassesConfirm = async () => {
    if (selectedClasses.length === 0) {
      setNotification({
        show: true,
        message: "Please select at least one class",
        title: "Error",
      });
      return;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Get all students assigned to the selected classes
      const studentIds: string[] = [];
      
      for (const classId of selectedClasses) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/assigned-to-class/${classId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.ok) {
          const students = await response.json();
          // Get student IDs
          const classStudentIds = students.map((s: any) => s.id);
          studentIds.push(...classStudentIds);
        }
      }

      // Remove duplicates and filter to only include students that exist in recipients
      const uniqueStudentIds = Array.from(new Set(studentIds));
      const validStudentIds = uniqueStudentIds.filter(id => 
        recipients.some(r => r.id === id && r.type === "student")
      );
      
      // Add to selectedRecipientNames for display in main view
      const selectedNames = recipients
        .filter((r) => validStudentIds.includes(r.id))
        .map((r) => r.name);
      setSelectedRecipientNames(prev => {
        const newNames = [...prev, ...selectedNames];
        // Remove duplicates
        return Array.from(new Set(newNames));
      });
      
      setShowClassModal(false);
      setSelectedClasses([]);

      setNotification({
        show: true,
        message: `Added ${validStudentIds.length} students from selected classes`,
        title: "Success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load students from selected classes",
        title: "Error",
      });
    }
  };

  // Event handlers
  const handleEventModalOpen = () => {
    setShowEventModal(true);
  };

  const handleEventModalClose = () => {
    setShowEventModal(false);
    setSelectedEvents([]);
  };

  const handleEventSelect = (eventId: string) => {
    setSelectedEvents((prev) => {
      if (prev.includes(eventId)) {
        return prev.filter((id) => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const handleSelectAllEvents = () => {
    setSelectedEvents(filteredEvents.map((event) => event.id));
  };

  const handleEventsConfirm = async () => {
    if (selectedEvents.length === 0) {
      setNotification({
        show: true,
        message: "Please select at least one event",
        title: "Error",
      });
      return;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Get all students registered for the selected events
      const studentPhones: string[] = [];
      
      for (const eventId of selectedEvents) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${eventId}/registrations`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.ok) {
          const registrations = await response.json();
          // Extract phone numbers from registration data
          const phones = registrations
            .map((reg: any) => {
              // Handle different possible structures of registration_data
              const data = reg.registration_data;
              if (typeof data === 'object' && data !== null) {
                // Try different possible field names for phone
                return data.phone || data.phone_number || data.telephone || data.mobile || data.cell || data.contact_phone;
              }
              return null;
            })
            .filter((phone: string) => phone); // Just filter out null/undefined phones
          studentPhones.push(...phones);
        }
      }

      // Remove duplicates
      const uniquePhones = Array.from(new Set(studentPhones));
      
      // Try to match phone numbers to existing recipients to show names
      const matchedRecipientIds: string[] = [];
      const unmatchedPhones: string[] = [];
      
      for (const phone of uniquePhones) {
        const matchingRecipient = recipients.find(r => r.phone === phone);
        if (matchingRecipient) {
          matchedRecipientIds.push(matchingRecipient.id);
        } else {
          unmatchedPhones.push(phone);
        }
      }
      
      // Add matched recipients to selectedRecipientNames (to show names)
      if (matchedRecipientIds.length > 0) {
        const selectedNames = recipients
          .filter((r) => matchedRecipientIds.includes(r.id))
          .map((r) => r.name);
        setSelectedRecipientNames(prev => {
          const newNames = [...prev, ...selectedNames];
          // Remove duplicates
          return Array.from(new Set(newNames));
        });
      }
      
      // Add unmatched phones to selectedRecipientPhones (to ensure they still get messages)
      if (unmatchedPhones.length > 0) {
        setSelectedRecipientPhones(prev => {
          const newPhones = [...prev, ...unmatchedPhones];
          // Remove duplicates
          return Array.from(new Set(newPhones));
        });
      }
      
      setShowEventModal(false);
      setSelectedEvents([]);

      const totalAdded = matchedRecipientIds.length + unmatchedPhones.length;
      setNotification({
        show: true,
        message: `Added ${totalAdded} students from selected events (${matchedRecipientIds.length} with names, ${unmatchedPhones.length} phone-only)`,
        title: "Success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load students from selected events",
        title: "Error",
      });
    }
  };

  const handleAIModalClose = () => {
    setIsAIModalOpen(false);
    setAIInput("");
    setAIOutput("");
  };

  const handleAIWrite = async () => {
    if (!studioUser) {
      return;
    }

    // Enhanced AI input validation
    if (!aiInput.trim()) {
      setNotification({
        show: true,
        message: "Please enter some text to generate content",
        title: "Error",
      });
      return;
    }

    // Enhanced AI input validation
    if (!validateInput(aiInput, 'aiInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'aiInput', value: aiInput.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid input content. Please check your text and try again.",
        title: "Error",
      });
      return;
    }

    // Client-side rate limiting for AI requests
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before making another AI request",
      });
      return;
    }

    try {
      setIsGenerating(true);
      setLastSubmissionTime(now);
      
      const authToken = getAuthToken();
      if (!authToken) {
        setNotification({
          show: true,
          message: "Please log in to use AI features",
          title: "Authentication Required",
        });
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            messages: [
              {
                role: "system",
                content:
                  "You write short, simple SMS texts for a martial arts studio to send to students and parents. Use everyday language. One or two short sentences. Stay under 160 characters when possible. Plain text only. No subject line, no quotes, and no extra commentary.",
              },
              {
                role: "user",
                content: `Write a simple SMS about: ${sanitizeInput(aiInput, "aiInput")}`,
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error(
            errorData.error ||
              "AI rate limit exceeded. Please wait before trying again."
          );
        } else if (response.status === 401) {
          throw new Error(
            "Authentication expired. Please refresh the page and try again."
          );
        } else {
          throw new Error(errorData.error || "Failed to generate content");
        }
      }

      const data = await response.json();
      setAIOutput(data.content || "");
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to generate SMS content. Please try again.",
        title: "Error",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseInEditor = () => {
    setMessageContent(aiOutput);
    handleAIModalClose();
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(aiOutput);
  };

  const handleDiscard = () => {
    setMessageContent("");
    setSelectedRecipients([]);
    setSelectedRecipientNames([]);
    setSelectedRecipientPhones([]);
    setPhoneRecipientLabels({});
    setShowDiscardModal(false);
  };

  const cancelDiscard = () => {
    setShowDiscardModal(false);
  };

  const handleSaveTemplate = async () => {
    // Enhanced template name validation
    if (!templateName.trim()) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please enter a template name",
      });
      return;
    }

    // Enhanced template name validation
    if (!validateInput(templateName, 'templateName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'templateName', value: templateName });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid template name. Please use only letters, numbers, spaces, hyphens, and underscores.",
      });
      return;
    }

    if (smsTemplatesLimitReached) {
      setNotification({
        show: true,
        message: `You can only create up to ${SMS_TEMPLATES_LIMIT} SMS templates per studio.`,
        title: "Limit Reached",
      });
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before saving another template",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveTemplate' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-templates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            name: sanitizeInput(templateName, 'templateName'),
            template: sanitizeInput(messageContent, 'messageContent'),
            studio_id: studioUser?.studio_id,
            csrfToken,
          }),
        }
      );

      if (response.ok) {
        const newTemplate = await response.json();
        setTemplates((prev) => [...prev, newTemplate]);
        setShowSaveTemplateModal(false);
        setTemplateName("");
        setNotification({
          show: true,
          title: "Success",
          message: "Template saved successfully!",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to save template",
      });
    }
  };

  const handleCancelSaveTemplate = () => {
    setShowSaveTemplateModal(false);
    setTemplateName("");
  };

  const handleTemplateSelect = (template: SMSTemplate) => {
    setMessageContent(template.template);
    setShowTemplatesModal(false);
  };

  const handleScheduleSend = async () => {
    // Enhanced date and time validation
    if (!scheduledDate || !scheduledTime) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please select date and time",
      });
      return;
    }

    // Enhanced date and time format validation
    if (!validateInput(scheduledDate, 'scheduledDate')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'scheduledDate', value: scheduledDate });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid date format. Please use MM/DD/YYYY format.",
      });
      return;
    }

    if (!validateInput(scheduledTime, 'scheduledTime')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'scheduledTime', value: scheduledTime });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid time format. Please use HH:MM format.",
      });
      return;
    }

    if (selectedRecipientNames.length === 0 && selectedRecipientPhones.length === 0) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please select at least one recipient before scheduling your message.",
      });
      return;
    }

    // Enhanced message content validation
    if (!validateInput(messageContent, 'messageContent')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'messageContent', value: messageContent.substring(0, 50) + '...' });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid message content. Please check your message and try again.",
      });
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before scheduling another SMS",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'scheduleSMS' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    // Check if SMS is configured before scheduling
    if (!smsConfig.isConfigured) {
      setNotification({
        show: true,
        title: "Error",
        message:
          "SMS is not configured. Please set up your SMS integration in Settings → Integrations.",
      });
      return;
    }

    // Convert MM/DD/YYYY to YYYY-MM-DD and combine with time
    const isoDate = convertDateToISO(scheduledDate);
    const fullTime = `${scheduledTime} ${scheduledAMPM}`;
    // Format as YYYY-MM-DDTHH:MM AM/PM for proper parsing
    const scheduledDateTime = `${isoDate} ${fullTime}`;

    try {
      // Prepare recipients and tag data
      // Get recipient IDs from the selected names
      const selectedRecipientIds = recipients
        .filter((r) => selectedRecipientNames.includes(r.name))
        .map((r) => r.id);
      
      const allRecipients = [...selectedRecipientIds];
      const tagDataByRecipient: Record<string, Record<string, string>> = {};

      // Handle recipients with IDs (from individual selection)
      for (const recipientId of selectedRecipientIds) {
        const recipient = recipients.find((r) => r.id === recipientId);
        const isStudent = recipient?.type === "student";
        const isLead = recipient?.type === "lead";
        const isStaff = recipient?.type === "staff";

        const tagData: Record<string, string> = {
          // camelCase tags
          studioName: studio?.name || "",
          studioAddress: studio?.address || "",
          studioPhone: studio?.phone || "",
          studioEmail: studio?.email || "",
          studentName: isStudent ? recipient?.name || "" : "",
          leadName: isLead ? recipient?.name || "" : "",
          leadEmail: isLead ? recipient?.email || "" : "",
          staffName: isStaff ? recipient?.name || "" : "",
          staffEmail: isStaff ? recipient?.email || "" : "",
          beltRank: isStudent ? recipient?.belt || "" : "",
          // snake_case tags
          studio_name: studio?.name || "",
          studio_address: studio?.address || "",
          studio_phone: studio?.phone || "",
          studio_email: studio?.email || "",
          student_name: isStudent ? recipient?.name || "" : "",
          lead_name: isLead ? recipient?.name || "" : "",
          lead_email: isLead ? recipient?.email || "" : "",
          staff_name: isStaff ? recipient?.name || "" : "",
          staff_email: isStaff ? recipient?.email || "" : "",
          belt_rank: isStudent ? recipient?.belt || "" : "",
          // Add recipient type for smart tag replacement
          _recipient_type: recipient?.type || "unknown",
          _recipient_id: recipient?.id || "",
        };

        tagDataByRecipient[recipientId] = tagData;
      }

      // Handle phone-only recipients (from class/event selection)
      for (const phone of selectedRecipientPhones) {
        const phoneId = `phone_${phone}`; // Create a unique ID for phone-only recipients
        allRecipients.push(phoneId);
        
        const tagData: Record<string, string> = {
          // camelCase tags
          studioName: studio?.name || "",
          studioAddress: studio?.address || "",
          studioPhone: studio?.phone || "",
          studioEmail: studio?.email || "",
          studentName: "", // No name available for phone-only recipients
          leadName: "", // No name available for phone-only recipients
          leadEmail: "", // No email available for phone-only recipients
          beltRank: "", // No belt available for phone-only recipients
          // snake_case tags
          studio_name: studio?.name || "",
          studio_address: studio?.address || "",
          studio_phone: studio?.phone || "",
          studio_email: studio?.email || "",
          student_name: "", // No name available for phone-only recipients
          lead_name: "", // No name available for phone-only recipients
          lead_email: "", // No email available for phone-only recipients
          belt_rank: "", // No belt available for phone-only recipients
        };

        tagDataByRecipient[phoneId] = tagData;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/schedule-sms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            content: sanitizeInput(messageContent, 'messageContent'),
            recipients: allRecipients,
            scheduled_time: scheduledDateTime,
            studio_id: studioUser?.studio_id,
            timezone: studioTimezone, // Add studio timezone for proper scheduling
            tagDataByRecipient,
            phoneOnlyRecipients: selectedRecipientPhones, // Add phone-only recipients separately
            csrfToken,
          }),
        }
      );

      if (response.ok) {
        // Format the scheduled time for display
        const scheduledDateObj = new Date(scheduledDateTime);
        const formattedDate = scheduledDateObj.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        const formattedTime = scheduledDateObj.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          timeZoneName: 'short'
        });
        
        setNotification({
          show: true,
          title: "SMS Scheduled",
          message: `SMS will be sent on ${formattedDate} at ${formattedTime}`,
        });
        setShowScheduleModal(false);
        setScheduledDate("");
        setScheduledTime("");
        setScheduledAMPM("AM");
        // Don't automatically open recipients modal after scheduling - just reset the form
        setMessageContent("");
        setSelectedRecipients([]);
        setSelectedRecipientPhones([]);
        setPhoneRecipientLabels({});
      }
    } catch (error) {
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to schedule SMS",
      });
    }
  };

  const handleCancelSchedule = () => {
    setShowScheduleModal(false);
    setScheduledDate("");
    setScheduledTime("");
    setScheduledAMPM("AM");
  };

  const handleDismissComplianceNotice = () => {
    setShowComplianceNotice(false);
    localStorage.setItem('sms_compliance_notice_dismissed', 'true');
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before deleting another template",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteTemplate' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-templates/${templateToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        setTemplates((prev) =>
          prev.filter((t) => t.id !== templateToDelete.id)
        );
        setTemplateToDelete(null);
        setNotification({
          show: true,
          title: "Success",
          message: "Template deleted successfully!",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to delete template",
      });
    }
  };

  const filteredRecipients = recipients.filter((recipient) => {
    const matchesSearch = recipient.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || recipient.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const filteredClasses = classes.filter(
    (classItem) =>
      classItem.class_name.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
      (classItem.days && classItem.days.some(day => 
        day.toLowerCase().includes(classSearchTerm.toLowerCase())
      ))
  );

  const filteredEvents = events.filter(
    (event) =>
      event.event_name.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
      event.type.toLowerCase().includes(eventSearchTerm.toLowerCase())
  );

  const filteredTemplates = templates.filter((template) =>
    template.name.toLowerCase().includes(templateSearchTerm.toLowerCase())
  );

  return (
    <PlanGatedPage
      capability="messagingSms"
      title="Compose SMS"
      subtitle="Send text messages to your students and leads"
    >
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white 2xl:pt-6">
              SMS Messaging
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Send text messages to your students and leads
            </p>
          </div>

        </div>

        {/* SMS Compliance Notice - Dismissible */}
        {showComplianceNotice && (
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  ⚠️ Important: SMS Compliance Requirements
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>
                    Before sending SMS messages, you must understand your legal responsibilities regarding consent, opt-outs, and do-not-contact lists. 
                    Violations can result in fines of $500-$1,500 per message from Twilio.
                  </p>
                  <Link
                    to="/app/settings/integrations"
                    className="inline-flex items-center mt-2 text-sm font-semibold text-red-800 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100 underline"
                  >
                    View Full SMS Compliance Policy →
                  </Link>
                </div>
              </div>
              <div className="ml-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleDismissComplianceNotice}
                  className="inline-flex rounded-md bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-red-50 dark:focus:ring-offset-red-900/20"
                  title="Dismiss notice"
                >
                  <span className="sr-only pointer-events-none">Dismiss</span>
                  <XMarkIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SMS Connection Banner - Only show if check is complete and not connected */}
        {smsCheckComplete && !smsConfig.isConfigured && (
          <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded flex flex-col items-center text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Connect SMS to Send Messages
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Set up SMS to send messages to students and leads. Choose to rent a phone number from us (instant setup) or use your own Twilio account.
            </p>
            <Link
              to="/app/settings/integrations"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go to Integrations Settings
            </Link>
          </div>
        )}

        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow">
          {/* Recipients Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                Recipients
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleClassModalOpen}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                >
                  <AcademicCapIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  <span className="pointer-events-none">Select by Class</span>
                </button>
                <button
                  onClick={handleEventModalOpen}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                >
                  <CalendarDaysIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  <span className="pointer-events-none">Select by Event</span>
                </button>
                <button
                  onClick={handleSelectRecipients}
                  disabled={loadingRecipients}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingRecipients ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b border-indigo-600 mr-2 pointer-events-none"></div>
                  ) : (
                    <PlusIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  )}
                  <span className="pointer-events-none">{loadingRecipients ? "Loading..." : "Select Recipients"}</span>
                </button>
              </div>
            </div>
            <div className="mt-1">
              <div className="relative">
                <div className="min-h-[42px] px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  {selectedRecipientNames.length > 0 || selectedRecipientPhones.length > 0 ? (
                    <div className="flex flex-wrap gap-1 items-center">
                      {selectedRecipientNames.map((name, index) => {
                        const recipient = recipients.find((r) => r.name === name);
                        const phoneLabel = recipient?.phone
                          ? formatPhoneDisplayDashes(recipient.phone)
                          : null;
                        return (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300"
                          >
                            {phoneLabel ? `${name} · ${phoneLabel}` : name}
                            <button
                              onClick={() =>
                                setSelectedRecipientNames((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              className="ml-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                      {selectedRecipientPhones.map((phone, index) => {
                        const label = phoneRecipientLabels[phoneMatchKey(phone)];
                        const phoneLabel = formatPhoneDisplayDashes(phone);
                        return (
                        <span
                          key={`phone-${index}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                        >
                          {label ? `${label} · ${phoneLabel}` : phoneLabel}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const key = phoneMatchKey(phone);
                              setSelectedRecipientPhones((prev) =>
                                prev.filter((_, i) => i !== index)
                              );
                              setPhoneRecipientLabels((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              });
                            }}
                            className="ml-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                          >
                            <XMarkIcon className="h-3 w-3 pointer-events-none" />
                          </button>
                        </span>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-gray-400 dark:text-gray-500 text-sm">
                      Select recipients
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Message Section */}
          <div className="flex-1 p-4">
            <div className="mb-4">
              <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Tooltip text="You can use student, lead, and staff tags in the same message! The system automatically filters tags based on recipient type, so you can send one message to mixed groups without creating multiple versions.">
                    <span className="cursor-help">
                      Message
                    </span>
                  </Tooltip>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setShowTagsModal(true)}
                  className="px-3 py-2 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm font-semibold transition"
                >
                  Insert Tags
                </button>
                <button
                  onClick={() => setShowTemplatesModal(true)}
                  className="px-3 py-2 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm font-semibold transition"
                >
                  Templates
                </button>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 xl:hidden">
                  {charCount}/160 ({messageCount} message
                  {messageCount !== 1 ? "s" : ""})
                </span>
              </div>
              <div className="hidden xl:block text-sm text-gray-500 dark:text-gray-400">
                {charCount}/160 ({messageCount} message
                {messageCount !== 1 ? "s" : ""})
              </div>
              </div>
              <textarea
                value={messageContent}
                onChange={(e) => {
                  const sanitizedValue = sanitizeInput(e.target.value, 'messageContent');
                  if (sanitizedValue.length <= MAX_LENGTHS.messageContent) {
                    setMessageContent(sanitizedValue);
                  }
                }}
                className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                placeholder="Type your SMS message here... You can use tags like {{student_name}}, {{studio_name}}, {{class_time}}, etc."
                maxLength={MAX_LENGTHS.messageContent}
              />
          </div>

          {/* Action Buttons */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-wrap gap-2 items-center">
              <button
                onClick={() => setShowScheduleModal(true)}
                disabled={(selectedRecipientNames.length === 0 && selectedRecipientPhones.length === 0)}
                title={(selectedRecipientNames.length === 0 && selectedRecipientPhones.length === 0) ? "Please select at least one recipient to schedule your message" : ""}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                  (selectedRecipientNames.length === 0 && selectedRecipientPhones.length === 0)
                    ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <ClockIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Schedule</span>
              </button>
              <button
                onClick={() => {
                  if (!smsTemplatesLimitReached) {
                    setShowSaveTemplateModal(true);
                  } else {
                    setNotification({
                      show: true,
                      message: `You can only create up to ${SMS_TEMPLATES_LIMIT} SMS templates per studio.`,
                      title: "Limit Reached",
                    });
                  }
                }}
                disabled={smsTemplatesLimitReached}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                  smsTemplatesLimitReached
                    ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
                title={smsTemplatesLimitReached ? `You can only create up to ${SMS_TEMPLATES_LIMIT} SMS templates per studio.` : ""}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Save Template {templates.length}/{SMS_TEMPLATES_LIMIT}</span>
              </button>
              <button
                onClick={() => setShowDiscardModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <TrashIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Discard</span>
              </button>
              <button
                onClick={() => setIsAIModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <SparklesIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Write with AI</span>
              </button>
              <button
                onClick={handleSend}
                disabled={
                  isSending ||
                  !messageContent.trim() ||
                  (selectedRecipientNames.length === 0 && selectedRecipientPhones.length === 0)
                }
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 pointer-events-none"></div>
                ) : (
                  <PhoneIcon className="h-4 w-4 mr-2 pointer-events-none" />
                )}
                <span className="pointer-events-none">{isSending ? "Sending..." : "Send SMS"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* AI Writing Modal */}
        {isAIModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-xl xl:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleAIModalClose}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Write with AI
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="ai-input"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          What would you like to write about?
                        </label>
                        <div className="mt-1 relative">
                          <textarea
                            id="ai-input"
                            rows={4}
                            value={aiInput}
                            onChange={(e) => {
                              const sanitizedValue = sanitizeInput(e.target.value, 'aiInput');
                              if (sanitizedValue.length <= MAX_LENGTHS.aiInput) {
                                setAIInput(sanitizedValue);
                              }
                            }}
                            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="e.g. class canceled tonight, makeup Thursday"
                            maxLength={MAX_LENGTHS.aiInput}
                          />
                          <div className="absolute bottom-2 right-2 text-xs text-gray-500 dark:text-gray-400">
                            {aiInput.length} characters
                          </div>
                        </div>
                      </div>
                      {isGenerating && (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                            Generating content...
                          </span>
                        </div>
                      )}
                      {aiOutput && (
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label
                              htmlFor="ai-output"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              AI Generated Content
                            </label>
                          </div>
                          <textarea
                            id="ai-output"
                            rows={6}
                            value={aiOutput}
                            readOnly
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-between gap-3 xl:gap-0">
                  <div className="flex flex-wrap gap-2">
                    {aiOutput && (
                      <>
                        <button
                          type="button"
                          onClick={handleCopyContent}
                          className="inline-flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4 mr-1 pointer-events-none" />
                          <span className="pointer-events-none">Copy</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleUseInEditor}
                          className="inline-flex items-center px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-1 pointer-events-none" />
                          <span className="pointer-events-none">Use in Editor</span>
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAIWrite}
                      disabled={isGenerating}
                      className={`inline-flex justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto ${
                        isGenerating ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <span className="pointer-events-none">{isGenerating ? "Generating..." : "Generate"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleAIModalClose}
                      className="inline-flex justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recipient Selection Modal */}
        {isRecipientModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleRecipientModalClose}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                      Select Recipients
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Choose students, leads, and staff—or add a phone number manually.
                    </p>
                    <div className="mt-4">
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Search recipients..."
                              value={searchTerm}
                              onChange={(e) => {
                                const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
                                if (sanitizedValue.length <= MAX_LENGTHS.searchTerm) {
                                  setSearchTerm(sanitizedValue);
                                }
                              }}
                              className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={handleSelectAll}
                            className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-4 py-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                          >
                            <span className="pointer-events-none">Select All</span>
                          </button>
                        </div>
                        <div className="flex items-center space-x-4">
                          <label className="text-sm text-gray-700 dark:text-gray-300">
                            Filter by:
                          </label>
                          <div className="flex items-center space-x-4">
                            <button
                              type="button"
                              onClick={() => setTypeFilter("all")}
                              className={`px-3 py-1 text-sm rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 font-semibold hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition ${
                                typeFilter === "all"
                                  ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600"
                                  : ""
                              }`}
                            >
                              <span className="pointer-events-none">All</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setTypeFilter("student")}
                              className={`px-3 py-1 text-sm rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition ${
                                typeFilter === "student"
                                  ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400"
                                  : ""
                              }`}
                            >
                              <span className="pointer-events-none">Students</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setTypeFilter("lead")}
                              className={`px-3 py-1 text-sm rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition ${
                                typeFilter === "lead"
                                  ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400"
                                  : ""
                              }`}
                            >
                              <span className="pointer-events-none">Leads</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setTypeFilter("staff")}
                              className={`px-3 py-1 text-sm rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition ${
                                typeFilter === "staff"
                                  ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400"
                                  : ""
                              }`}
                            >
                              <span className="pointer-events-none">Staff</span>
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
                          <div className="flex-1 min-w-0">
                            <label
                              htmlFor="custom-recipient-phone"
                              className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                            >
                              Add custom phone
                            </label>
                            <input
                              id="custom-recipient-phone"
                              type="tel"
                              inputMode="numeric"
                              autoComplete="tel"
                              placeholder="555-123-4567"
                              value={customPhoneInput}
                              onChange={(e) => {
                                const formatted = formatCustomPhoneInputAsTyped(
                                  e.target.value
                                );
                                if (formatted.length <= MAX_LENGTHS.customPhone) {
                                  setCustomPhoneInput(formatted);
                                }
                              }}
                              maxLength={MAX_LENGTHS.customPhone}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddCustomPhoneToSelection();
                                }
                              }}
                              className="block w-full rounded-md border-0 py-1.5 pl-3 pr-3 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={handleAddCustomPhoneToSelection}
                              className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 sm:w-auto sm:min-w-[5rem]"
                            >
                              <span className="pointer-events-none">Add</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                        {loadingRecipients ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : filteredRecipients.length === 0 ? (
                          <div className="text-center py-8">
                            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                              No recipients found
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {searchTerm
                                ? "No recipients match your search"
                                : "No recipients available"}
                            </p>
                          </div>
                        ) : (
                          filteredRecipients.map((recipient) => (
                            <div
                              key={recipient.id}
                              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors duration-150"
                              onClick={() =>
                                handleRecipientSelect(recipient.id)
                              }
                            >
                              <div className="flex items-center min-w-0">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {recipient.name}
                                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                                      (
                                      {recipient.type === "student"
                                        ? "Student"
                                        : recipient.type === "lead"
                                        ? "Lead"
                                        : "Staff"}
                                      )
                                    </span>
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {formatPhoneDisplayDashes(recipient.phone)}
                                  </p>
                                </div>
                              </div>
                              {selectedRecipients.includes(recipient.id) && (
                                <CheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        onClick={handleRecipientsConfirm}
                        className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 sm:ml-3 sm:w-auto"
                      >
                        <span className="pointer-events-none">Confirm</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleRecipientModalClose}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                      >
                        <span className="pointer-events-none">Cancel</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tags Modal */}
        {showTagsModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-xl xl:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setShowTagsModal(false)}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Insert Tags
                    </h3>
                    <div className="mt-4">
                      {smsTags.length > 0 ? (
                        <ul className="space-y-2">
                          {smsTags
                            .filter(
                              (tag) =>
                                !(
                                  !hasCapability('stripeBilling') &&
                                  isPaymentLinkTag(tag.tag_key)
                                )
                            )
                            .map((tag) => (
                            <li key={tag.id}>
                              <button
                                className="w-full text-left px-3 py-2 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition flex items-center"
                                onClick={() => handleInsertTag(tag)}
                              >
                                <span className="font-mono text-indigo-700 dark:text-indigo-400 pointer-events-none">
                                  {"{{" + tag.tag_key + "}}"}
                                </span>
                                <span className="text-gray-600 dark:text-gray-400 ml-2 pointer-events-none">
                                  - {tag.tag_name}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-gray-500 dark:text-gray-400">
                            No SMS tags available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => setShowTagsModal(false)}
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Modal */}
        {showTemplatesModal && (
          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-xl xl:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={() => setShowTemplatesModal(false)}
              >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
              </button>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Templates
              </h3>
              <ul className="space-y-2">
                {templates.map((template) => (
                  <li
                    key={template.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <button
                      className="flex-1 text-left px-3 py-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <span className="font-semibold text-indigo-700 dark:text-indigo-400 pointer-events-none">
                        {template.name}
                      </span>
                    </button>
                    <button
                      onClick={() => setTemplateToDelete(template)}
                      className="ml-2 px-2 py-1 text-xs rounded border border-red-400 dark:border-red-600 bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-600 dark:hover:border-red-500 transition"
                    >
                      <span className="pointer-events-none">Delete</span>
                    </button>
                  </li>
                ))}
              </ul>
              </div>
            </div>
          </div>
        )}

        {/* Save Template Modal */}
        {showSaveTemplateModal && !smsTemplatesLimitReached && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Save as Template
                    </h3>
                    <div className="mt-4">
                      <label
                        htmlFor="template-name"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Template Name
                      </label>
                      <input
                        type="text"
                        id="template-name"
                        value={templateName}
                        onChange={(e) => {
                          const sanitizedValue = sanitizeInput(e.target.value, 'templateName');
                          if (sanitizedValue.length <= MAX_LENGTHS.templateName) {
                            setTemplateName(sanitizedValue);
                          }
                        }}
                        className="mt-1 block w-full rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Enter template name"
                        maxLength={MAX_LENGTHS.templateName}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleSaveTemplate}
                    disabled={!templateName.trim()}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Save Template</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelSaveTemplate}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                                      <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Schedule SMS
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Schedule your SMS to be sent at a specific time in your
                    studio's timezone ({studioTimezone}).
                  </p>
                  <div className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="schedule-date"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Date (MM/DD/YYYY)
                        </label>
                        <input
                          type="text"
                          id="schedule-date"
                          value={scheduledDate}
                          onChange={(e) => {
                            const formattedValue = formatDate(e.target.value);
                            if (formattedValue.length <= MAX_LENGTHS.scheduledDate) {
                              setScheduledDate(formattedValue);
                            }
                          }}
                          placeholder="MM/DD/YYYY"
                          className="mt-1 block w-full rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          maxLength={MAX_LENGTHS.scheduledDate}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="schedule-time"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Time - {studioTimezone}
                        </label>
                        <div className="mt-1 flex space-x-2">
                          <input
                            type="text"
                            id="schedule-time"
                            value={scheduledTime}
                            onChange={(e) => {
                              const formattedValue = formatTime(e.target.value);
                              if (formattedValue.length <= MAX_LENGTHS.scheduledTime) {
                                setScheduledTime(formattedValue);
                              }
                            }}
                            placeholder="HH:MM"
                            className="block w-full rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            maxLength={MAX_LENGTHS.scheduledTime}
                          />
                          <select
                            value={scheduledAMPM}
                            onChange={(e) => setScheduledAMPM(e.target.value)}
                            className="block w-20 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleScheduleSend}
                    disabled={!scheduledDate || !scheduledTime}

                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Schedule Send</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelSchedule}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discard Modal */}
        {showDiscardModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white text-left">
                      Discard Message
                    </h3>
                    <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-left">
                        Are you sure you want to discard this message? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    onClick={cancelDiscard}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscard}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="pointer-events-none">Discard</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Class Selection Modal */}
        {showClassModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleClassModalClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                      Select Classes
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Choose classes to add all enrolled students to your message
                    </p>
                    <div className="mt-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Search classes..."
                            value={classSearchTerm}
                            onChange={(e) => {
                              const sanitizedValue = sanitizeInput(e.target.value, 'classSearchTerm');
                              if (sanitizedValue.length <= MAX_LENGTHS.classSearchTerm) {
                                setClassSearchTerm(sanitizedValue);
                              }
                            }}
                            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAllClasses}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                        >
                          <span className="pointer-events-none">Select All Classes</span>
                        </button>
                      </div>
                      <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                        {loadingClasses ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : filteredClasses.length === 0 ? (
                          <div className="text-center py-8">
                            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                              {classSearchTerm ? "No classes match your search" : "No classes found"}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {classSearchTerm ? "Try adjusting your search terms" : "No classes available for this studio"}
                            </p>
                          </div>
                        ) : (
                          filteredClasses.map((classItem) => (
                            <div
                              key={classItem.id}
                              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors duration-150"
                              onClick={() => handleClassSelect(classItem.id)}
                            >
                              <div className="flex items-center min-w-0">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {classItem.class_name}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {classItem.days && classItem.days.length > 0 && (
                                      <span>{classItem.days.join(", ")}</span>
                                    )}
                                    {classItem.start_time && classItem.end_time && (
                                      <span className="ml-2">
                                        {formatTime12Hour(classItem.start_time)} - {formatTime12Hour(classItem.end_time)}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              {selectedClasses.includes(classItem.id) && (
                                <CheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        onClick={handleClassesConfirm}
                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                      >
                        <span className="pointer-events-none">Confirm</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleClassModalClose}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                      >
                        <span className="pointer-events-none">Cancel</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Selection Modal */}
        {showEventModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleEventModalClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                      Select Events
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Choose events to add all registrants to your message
                    </p>
                    <div className="mt-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Search events..."
                            value={eventSearchTerm}
                            onChange={(e) => {
                              const sanitizedValue = sanitizeInput(e.target.value, 'eventSearchTerm');
                              if (sanitizedValue.length <= MAX_LENGTHS.eventSearchTerm) {
                                setEventSearchTerm(sanitizedValue);
                              }
                            }}
                            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAllEvents}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                        >
                          <span className="pointer-events-none">Select All Events</span>
                        </button>
                      </div>
                      <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                        {loadingEvents ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : filteredEvents.length === 0 ? (
                          <div className="text-center py-8">
                            <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                              {eventSearchTerm ? "No events match your search" : "No events found"}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {eventSearchTerm ? "Try adjusting your search terms" : "No events available for this studio"}
                            </p>
                          </div>
                        ) : (
                          filteredEvents.map((event) => (
                            <div
                              key={event.id}
                              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors duration-150"
                              onClick={() => handleEventSelect(event.id)}
                            >
                              <div className="flex items-center min-w-0">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {event.event_name}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {formatDate(event.from_date)}
                                    {event.from_date !== event.end_date && (
                                      <span className="text-gray-400"> - {formatDate(event.end_date)}</span>
                                    )} • {event.type}
                                    {event.start_time && event.end_time && (
                                      <span className="ml-2">
                                        {formatTime12Hour(event.start_time)} - {formatTime12Hour(event.end_time)}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              {selectedEvents.includes(event.id) && (
                                <CheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        onClick={handleEventsConfirm}
                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                      >
                        <span className="pointer-events-none">Confirm</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleEventModalClose}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                      >
                        <span className="pointer-events-none">Cancel</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Template Confirmation Modal */}
        {templateToDelete && (
          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white text-left">
                      Delete Template
                    </h3>
                    <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-left">
                        Are you sure you want to delete the template "
                        {templateToDelete.name}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    onClick={() => setTemplateToDelete(null)}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteTemplate}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </PlanGatedPage>
  );
}
